import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";
import {
  MotorcycleProductIntroFormValues,
  MotorcycleTexts,
} from "../motorcycleInterface";

import { Paragraph } from "App/global-style";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { nextStep } from "Store/steps/stepsSlice";
import { TextLanguage } from "Store/texts/interfaces";
import { mapValidationErrors } from "Utils/formUtils";
import React from "react";
import { useDispatch } from "react-redux";
import { MOTORCYCLE_INTRO_STEP } from "..";
import { FormElements } from "./FormElements";
import { validationSchema } from "./motorcycleProductIntroValidation";

interface Props {
  readonly texts: MotorcycleTexts;
  readonly lang: TextLanguage;
}

export const MotorcycleProductIntro = (props: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { texts, lang } = props;

  const introFormState = useFormState<MotorcycleProductIntroFormValues>(
    MOTORCYCLE_INTRO_STEP.id,
    initialFormValues,
  );

  const handleSubmit = (form: MotorcycleProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: MOTORCYCLE_INTRO_STEP.id,
        formState: form,
      }),
    );

    dispatch(nextStep());
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.MOTORCYCLE}`,
        );
      }}
    >
      <ProductIntroTile
        initialValues={introFormState}
        onSubmit={handleSubmit}
        productBackendName={ProductBackendName.MOTORCYCLE}
        validationSchema={validationSchema()}
      >
        {(formProps: FormikProps<MotorcycleProductIntroFormValues>) => (
          <Form>
            <Paragraph>{texts.tile.productIntro.ingress[lang]}</Paragraph>
            <FormElements
              formProps={formProps}
              texts={texts.formText.checkPrice}
              lang={lang}
            />

            <PartnerProductIntroForms />

            <ErrorsSummedUp
              errors={mapValidationErrors(
                formProps.errors,
                texts.details,
                lang,
              )}
              isVisible
            />

            <MarketingConsent />
            <ScrollToFirstError />
            <NavigationButtons hidePreviousButton />
          </Form>
        )}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues = {
  year: undefined,
  brand: undefined,
  model: undefined,
  bonus: undefined,
};
